/**
 * @fileoverview Tasks Hooks
 *
 * Custom React hooks for fetching and managing tasks using React Query.
 * Includes hooks for task listings, single task operations, and CRUD mutations.
 *
 * @version 1.1.0
 * @updated 2025-12-12
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { httpsCallable } from "firebase/functions";
import { functions } from "../firebase";

// =============================================================================
// EXISTING TASK QUERIES
// =============================================================================

/**
 * Hook to get tasks for a specific project
 * @param {string} projectId - The project ID
 * @param {Object} options - Query options
 * @param {string} options.status - Filter by status (optional)
 * @param {string} options.phase - Filter by construction phase (optional)
 * @param {number} options.limit - Max number of records (optional)
 */
export function useProjectTasks(projectId, options = {}) {
  return useQuery({
    queryKey: ["project-tasks", projectId, options],
    queryFn: async () => {
      const getProjectTasks = httpsCallable(functions, "getTasks");
      const result = await getProjectTasks({ projectId, ...options });
      return result.data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 3, // 3 minutes
  });
}

/**
 * Hook to get the weekly digest (tasks completed in last 7 days)
 * @param {string} projectId - The project ID
 */
export function useWeeklyDigest(projectId) {
  return useQuery({
    queryKey: ["weekly-digest", projectId],
    queryFn: async () => {
      const getWeeklyDigest = httpsCallable(functions, "getWeeklyDigest");
      const result = await getWeeklyDigest({ projectId });
      return result.data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 10, // 10 minutes (digest doesn't change often)
  });
}

/**
 * Hook to get tasks filtered by status
 * @param {string} projectId - The project ID
 * @param {string} status - Task status to filter by
 */
export function useTasksByStatus(projectId, status) {
  return useQuery({
    queryKey: ["tasks-by-status", projectId, status],
    queryFn: async () => {
      const getTasksByStatus = httpsCallable(functions, "getTasksByStatus");
      const result = await getTasksByStatus({ projectId, status });
      return result.data;
    },
    enabled: !!projectId && !!status,
    staleTime: 1000 * 60 * 3, // 3 minutes
  });
}

/**
 * Hook to get a specific task by ID (basic version for client portal)
 * @param {string} taskId - The task ID to fetch
 */
export function useTask(taskId) {
  return useQuery({
    queryKey: ["task", taskId],
    queryFn: async () => {
      const getTask = httpsCallable(functions, "getTask");
      const result = await getTask({ taskId });
      return result.data;
    },
    enabled: !!taskId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Hook to get task statistics for a project
 * @param {string} projectId - The project ID
 */
export function useTaskStats(projectId) {
  return useQuery({
    queryKey: ["task-stats", projectId],
    queryFn: async () => {
      const getTaskStats = httpsCallable(functions, "getTaskStats");
      const result = await getTaskStats({ projectId });
      return result.data;
    },
    enabled: !!projectId,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// =============================================================================
// NEW: DETAILED TASK QUERY (for editing/admin)
// =============================================================================

/**
 * Hook to get a specific task with full details for editing
 * Returns more fields than useTask, including vendor info, attachments, etc.
 *
 * @param {string} taskId - The task ID to fetch
 * @returns {Object} Query result with detailed task data
 */
export function useTaskForEdit(taskId) {
  return useQuery({
    queryKey: ["task-edit", taskId],
    queryFn: async () => {
      const getTaskById = httpsCallable(functions, "getTaskById");
      const result = await getTaskById({ taskId });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch task");
      }

      return result.data.task;
    },
    enabled: !!taskId,
    staleTime: 1000 * 60 * 2, // 2 minutes - shorter for editing
    refetchOnWindowFocus: true,
  });
}

// =============================================================================
// NEW: TASK MUTATION HOOKS (for CRUD operations)
// =============================================================================

/**
 * Hook to update a task
 *
 * @returns {Object} Mutation object with mutate/mutateAsync functions
 */
export function useUpdateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, updates }) => {
      const updateTaskFn = httpsCallable(functions, "updateTask");
      const result = await updateTaskFn({ taskId, updates });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to update task");
      }

      return result.data.task;
    },
    onSuccess: (data, variables) => {
      // Update the specific task in cache
      queryClient.setQueryData(["task-edit", variables.taskId], data);
      queryClient.setQueryData(["task", variables.taskId], data);
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks-by-status"] });
      queryClient.invalidateQueries({ queryKey: ["task-stats"] });
      queryClient.invalidateQueries({ queryKey: ["weekly-digest"] });
      queryClient.invalidateQueries({ queryKey: ["adminAlerts"] });
      queryClient.invalidateQueries({ queryKey: ["calendarTasks"] });
    },
  });
}

/**
 * Hook to create a new task
 *
 * @returns {Object} Mutation object with mutate/mutateAsync functions
 */
export function useCreateTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskData) => {
      const createTaskFn = httpsCallable(functions, "createTask");
      const result = await createTaskFn({ taskData });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to create task");
      }

      return result.data.task;
    },
    onSuccess: () => {
      // Invalidate task-related queries
      queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks-by-status"] });
      queryClient.invalidateQueries({ queryKey: ["task-stats"] });
      queryClient.invalidateQueries({ queryKey: ["calendarTasks"] });
      queryClient.invalidateQueries({ queryKey: ["adminDashboardStats"] });
    },
  });
}

/**
 * Hook to delete a task
 *
 * @returns {Object} Mutation object with mutate/mutateAsync functions
 */
export function useDeleteTask() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId) => {
      const deleteTaskFn = httpsCallable(functions, "deleteTask");
      const result = await deleteTaskFn({ taskId });

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to delete task");
      }

      return result.data;
    },
    onSuccess: (_, taskId) => {
      // Remove from cache
      queryClient.removeQueries({ queryKey: ["task-edit", taskId] });
      queryClient.removeQueries({ queryKey: ["task", taskId] });
      // Invalidate related queries
      queryClient.invalidateQueries({ queryKey: ["project-tasks"] });
      queryClient.invalidateQueries({ queryKey: ["tasks-by-status"] });
      queryClient.invalidateQueries({ queryKey: ["task-stats"] });
      queryClient.invalidateQueries({ queryKey: ["calendarTasks"] });
      queryClient.invalidateQueries({ queryKey: ["adminDashboardStats"] });
      queryClient.invalidateQueries({ queryKey: ["adminAlerts"] });
    },
  });
}

// =============================================================================
// NEW: CALENDAR & ADMIN TASK QUERIES
// =============================================================================

/**
 * Hook to fetch tasks by date range (for calendar views)
 *
 * @param {Object} options - Date range options
 * @param {string} [options.startDate] - Start date (ISO string)
 * @param {string} [options.endDate] - End date (ISO string)
 * @returns {Object} Query result with tasks array
 */
export function useTasksByDateRange(options = {}) {
  return useQuery({
    queryKey: ["calendarTasks", options],
    queryFn: async () => {
      const getAllTasksForCalendar = httpsCallable(
        functions,
        "getAllTasksForCalendar"
      );
      const result = await getAllTasksForCalendar(options);

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch calendar tasks");
      }

      return result.data.tasks || [];
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
  });
}

/**
 * Hook to fetch overdue tasks
 *
 * @returns {Object} Query result with overdue tasks array
 */
export function useOverdueTasks() {
  return useQuery({
    queryKey: ["tasks", "overdue"],
    queryFn: async () => {
      const getOverdueTasks = httpsCallable(functions, "getOverdueTasks");
      const result = await getOverdueTasks();

      if (!result.data.success) {
        throw new Error(result.data.error || "Failed to fetch overdue tasks");
      }

      return result.data.tasks || [];
    },
    staleTime: 1000 * 60 * 2, // 2 minutes
    refetchOnWindowFocus: true,
  });
}

// =============================================================================
// COMBINED HOOK
// =============================================================================

/**
 * Hook that provides all task-related data and operations for a single task
 * Uses useTaskForEdit for detailed task data suitable for editing
 *
 * @param {string|null} taskId - Optional task ID for single task operations
 * @returns {Object} Combined task data and operations
 */
export function useTaskOperations(taskId = null) {
  const task = useTaskForEdit(taskId);
  const updateTask = useUpdateTask();
  const createTask = useCreateTask();
  const deleteTask = useDeleteTask();

  return {
    // Single task data
    task: task.data,
    isLoading: task.isLoading,
    error: task.error,

    // Mutations
    updateTask: updateTask.mutateAsync,
    createTask: createTask.mutateAsync,
    deleteTask: deleteTask.mutateAsync,

    // Mutation states
    isUpdating: updateTask.isPending,
    isCreating: createTask.isPending,
    isDeleting: deleteTask.isPending,
    updateError: updateTask.error,
    createError: createTask.error,
    deleteError: deleteTask.error,
  };
}

export default {
  useProjectTasks,
  useWeeklyDigest,
  useTasksByStatus,
  useTask,
  useTaskStats,
  useTaskForEdit,
  // Mutation hooks
  useUpdateTask,
  useCreateTask,
  useDeleteTask,
  // Query hooks
  useTasksByDateRange,
  useOverdueTasks,
  // Combined hook
  useTaskOperations,
};
